import { useEffect, useMemo, useState } from "react";
import { Loader2, AlertTriangle, ChevronDown, Check, Send, RotateCcw, X, ArrowRight, FileText } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  fetchArticles, fetchBriefQueue, fetchBrief,
  updateBriefStatus, updateArticle,
  type BriefQueueRow,
} from "@/lib/contentOps/api";
import type { ArticleRow, ContentBrief } from "@/lib/contentOps/types";
import BriefTab from "./tabs/BriefTab";
import { supabase } from "@/integrations/supabase/client";


interface Props { onOpenArticle: (id: string, defaultTab?: string) => void }

// Sections we expect a high-quality brief / article to cover (EGS-specific list, not Sinai content)
const REQUIRED_SECTIONS = [
  "Quick Answer", "Table of Contents", "Core Sections (H2)", "Practical Applications",
  "Common Mistakes", "Expert Insight", "Service Spotlight", "Key Takeaways", "FAQ",
];

// Compute heuristic Editorial Quality scores from existing data (no schema changes).
function scoreBrief(brief: ContentBrief | null, article: ArticleRow | null) {
  if (!brief || !article) {
    return {
      total: 0, label: "Not started",
      bars: [
        { key: "SEO", value: 0 },
        { key: "Readability", value: 0 },
        { key: "E-E-A-T", value: 0 },
        { key: "Internal Linking", value: 0 },
        { key: "Brief Depth", value: 0 },
        { key: "Audience Fit", value: 0 },
      ],
      missing: REQUIRED_SECTIONS,
      signals: ["Generate the brief to start scoring."],
    };
  }
  const seo = Math.min(100,
    (brief.primary_keyword ? 25 : 0) +
    (brief.target_keywords.length >= 3 ? 25 : brief.target_keywords.length * 8) +
    (brief.search_intent ? 20 : 0) +
    (article.meta_description ? 15 : 0) +
    (article.seo_title ? 15 : 0));
  const readability = Math.min(100,
    (brief.outline.length >= 5 ? 40 : brief.outline.length * 8) +
    (brief.angle ? 25 : 0) +
    (brief.quick_answer ? 35 : 0));
  const eeat = Math.min(100,
    (brief.target_audience ? 25 : 0) +
    (brief.competitor_urls.length >= 3 ? 25 : brief.competitor_urls.length * 8) +
    (brief.objective ? 25 : 0) +
    (article.author && article.author_role ? 25 : 0));
  const linking = Math.min(100,
    (article.related_slugs.length >= 4 ? 60 : article.related_slugs.length * 15) +
    (brief.outline.some((s) => s.target_entities?.length) ? 40 : 0));
  const depth = Math.min(100,
    (brief.outline.length >= 6 ? 50 : brief.outline.length * 8) +
    (brief.serp_summary?.content_gaps?.length ? 25 : 0) +
    (brief.cta_strategy ? 25 : 0));
  const audience = Math.min(100,
    (brief.target_audience ? 50 : 0) +
    (brief.angle ? 30 : 0) +
    (brief.objective ? 20 : 0));

  const bars = [
    { key: "SEO", value: seo },
    { key: "Readability", value: readability },
    { key: "E-E-A-T", value: eeat },
    { key: "Internal Linking", value: linking },
    { key: "Brief Depth", value: depth },
    { key: "Audience Fit", value: audience },
  ];
  const total = Math.round(bars.reduce((a, b) => a + b.value, 0) / bars.length);

  const missing: string[] = [];
  if (!brief.quick_answer) missing.push("Quick Answer");
  if (brief.outline.length < 3) missing.push("Table of Contents");
  if (brief.outline.length < 5) missing.push("Core Sections (H2)");
  if (!brief.outline.some((s) => /apply|practice|use|step/i.test((s.h2 ?? "")))) missing.push("Practical Applications");
  if (!brief.outline.some((s) => /mistake|avoid|pitfall/i.test((s.h2 ?? "")))) missing.push("Common Mistakes");
  if (!brief.outline.some((s) => /expert|insight|opinion/i.test((s.h2 ?? "")))) missing.push("Expert Insight");
  if (!brief.cta_strategy) missing.push("Service Spotlight");
  if (article.key_takeaways.length === 0) missing.push("Key Takeaways");
  if (article.faqs.length === 0) missing.push("FAQ");

  const signals: string[] = [];
  if (!brief.primary_keyword) signals.push("Primary keyword is missing from the brief.");
  if (brief.target_keywords.length < 3) signals.push("Add at least 3 secondary keywords for topical coverage.");
  if (!brief.target_audience) signals.push("Define the target audience to sharpen tone and angle.");
  if (!brief.quick_answer) signals.push("Add a 40-60 word Quick Answer for AI snippet eligibility.");
  if (brief.outline.length < 5) signals.push("Outline has fewer than 5 H2s, expand for comprehensive coverage.");
  if (article.related_slugs.length < 3) signals.push("Plan at least 3 internal links to related EGS articles.");
  if (article.faqs.length < 3) signals.push("Add 3+ FAQs to capture long-tail search queries.");

  const label = total >= 80 ? "Ready" : total >= 60 ? "Solid" : total >= 40 ? "Needs work" : "Not ready";
  return { total, label, bars, missing, signals };
}

const BriefGeneratorView = ({ onOpenArticle }: Props) => {
  const [articles, setArticles] = useState<ArticleRow[]>([]);
  const [briefs, setBriefs] = useState<BriefQueueRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [brief, setBrief] = useState<ContentBrief | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([fetchArticles(), fetchBriefQueue()]);
      setArticles(a);
      setBriefs(b);
      if (!selectedId) {
        // Default to first brief in review, else first brief, else first article
        const first = b.find((r) => r.brief.status === "in_review") ?? b[0];
        setSelectedId(first?.article.id ?? a[0]?.id ?? null);
      }
    } catch (e) {
      toast.error("Failed to load briefs", { description: (e as Error).message });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const article = useMemo(() => articles.find((a) => a.id === selectedId) ?? null, [articles, selectedId]);

  useEffect(() => {
    if (!selectedId) { setBrief(null); return; }
    fetchBrief(selectedId).then(setBrief).catch(() => setBrief(null));
  }, [selectedId, reloadKey]);

  // Subscribe to brief changes so sidecar re-scores when BriefTab persists.
  useEffect(() => {
    if (!selectedId) return;
    const ch = supabase
      .channel(`brief-gen-${selectedId}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "content_briefs", filter: `article_id=eq.${selectedId}` },
        () => setReloadKey((k) => k + 1))
      .on("postgres_changes",
        { event: "UPDATE", schema: "public", table: "blog_articles", filter: `id=eq.${selectedId}` },
        () => load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [selectedId]);

  const score = useMemo(() => scoreBrief(brief, article), [brief, article]);

  const topicOptions = useMemo(() => {
    // Prefer articles that already have briefs at top
    const briefIds = new Set(briefs.map((b) => b.article.id));
    return [...articles].sort((a, b) => {
      const ah = briefIds.has(a.id) ? 0 : 1;
      const bh = briefIds.has(b.id) ? 0 : 1;
      if (ah !== bh) return ah - bh;
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    });
  }, [articles, briefs]);

  if (loading && !article) {
    return (
      <div className="flex items-center justify-center h-48" style={{ color: "hsl(0 0% 50%)" }}>
        <Loader2 size={18} className="animate-spin mr-2" /> Loading briefs…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Topic selector bar */}
      <div className="rounded-xl border p-4 flex flex-wrap items-center gap-3"
        style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
        <span className="text-[10px] uppercase tracking-[0.18em] font-semibold"
          style={{ color: "hsl(0 0% 55%)" }}>Topic</span>
        <Select value={selectedId ?? ""} onValueChange={setSelectedId}>
          <SelectTrigger className="h-10 min-w-[320px] flex-1 max-w-2xl border"
            style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 22%)", color: "hsl(0 0% 95%)" }}>
            <SelectValue placeholder="Pick an article / brief…" />
            <ChevronDown size={14} style={{ color: "hsl(0 0% 55%)" }} />
          </SelectTrigger>
          <SelectContent>
            {topicOptions.map((a) => {
              const hasBrief = briefs.some((b) => b.article.id === a.id);
              return (
                <SelectItem key={a.id} value={a.id}>
                  {hasBrief ? "● " : "○ "}{a.title || "Untitled"} · {a.category}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {article && (
          <button onClick={() => onOpenArticle(article.id)}
            className="text-xs underline-offset-2 hover:underline"
            style={{ color: "hsl(207 68% 75%)" }}>Open full workspace →</button>
        )}
      </div>

      {!article ? (
        <div className="rounded-xl border p-12 text-center"
          style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
          <p className="text-base font-medium" style={{ color: "hsl(0 0% 80%)" }}>No topic selected</p>
          <p className="text-sm mt-1" style={{ color: "hsl(0 0% 55%)" }}>
            Create a topic in the Topics Library to generate a brief.
          </p>
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-3">
          {/* Brief form + Sinai-style stage rail */}
          <div className="lg:col-span-2 min-w-0 space-y-4">
            <StageRail
              brief={brief}
              article={article}
              onAction={async (action) => {
                try {
                  if (!brief && action !== "noop") {
                    toast.info("Generate the brief first using the form below.");
                    return;
                  }
                  if (action === "submit" && brief) {
                    await updateBriefStatus(brief.id, "in_review");
                    toast.success("Brief submitted for review");
                  } else if (action === "approve" && brief) {
                    // Collapsed flow: draft -> in_review -> approved in one click.
                    if (brief.status === "draft") {
                      await updateBriefStatus(brief.id, "in_review");
                    }
                    await updateBriefStatus(brief.id, "approved");
                    if (article.status === "brief" || article.status === "idea" || article.status === "research") {
                      await updateArticle(article.id, { status: "draft" });
                    }
                    toast.success("Approved, opening draft");
                    // Auto-advance: open the workspace directly on the Draft tab.
                    onOpenArticle(article.id, "draft");
                    return;
                  } else if (action === "changes" && brief) {
                    await updateBriefStatus(brief.id, "draft", "Needs changes");
                    toast.success("Sent back for changes");
                  } else if (action === "reject" && brief) {
                    await updateBriefStatus(brief.id, "rejected");
                    toast.success("Brief rejected");
                  }
                  setReloadKey((k) => k + 1);
                  load();
                } catch (e) {
                  toast.error("Action failed", { description: (e as Error).message });
                }
              }}
            />
            <BriefTab
              key={article.id}
              article={article}
              onArticleChange={(a) => {
                setArticles((prev) => prev.map((x) => x.id === a.id ? a : x));
                setReloadKey((k) => k + 1);
              }}
            />
          </div>


          {/* Sinai-style Editorial Quality sidecar */}
          <aside className="lg:col-span-1 space-y-4">
            <div className="rounded-xl border p-5"
              style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-2"
                style={{ color: "hsl(0 0% 55%)" }}>Editorial Quality</div>
              <div className="flex items-end justify-between">
                <div>
                  <span className="text-5xl font-bold" style={{ color: "hsl(0 0% 97%)" }}>{score.total}</span>
                  <span className="text-sm ml-1" style={{ color: "hsl(0 0% 55%)" }}>/100</span>
                </div>
                <span className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold"
                  style={{
                    background: score.total >= 80 ? "hsl(142 70% 35% / 0.18)"
                      : score.total >= 60 ? "hsl(180 70% 40% / 0.18)"
                      : score.total >= 40 ? "hsl(38 92% 50% / 0.18)"
                      : "hsl(0 75% 45% / 0.18)",
                    color: score.total >= 80 ? "hsl(142 70% 70%)"
                      : score.total >= 60 ? "hsl(180 70% 70%)"
                      : score.total >= 40 ? "hsl(38 92% 70%)"
                      : "hsl(0 75% 70%)",
                  }}>{score.label}</span>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-x-5 gap-y-3">
                {score.bars.map((b) => (
                  <div key={b.key}>
                    <div className="flex items-center justify-between text-[11px] mb-1">
                      <span style={{ color: "hsl(0 0% 65%)" }}>{b.key}</span>
                      <span className="font-semibold tabular-nums" style={{ color: "hsl(0 0% 90%)" }}>{b.value}</span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "hsl(0 0% 14%)" }}>
                      <div className="h-full rounded-full transition-all"
                        style={{
                          width: `${b.value}%`,
                          background:
                            b.value >= 70 ? "hsl(142 70% 50%)"
                            : b.value >= 45 ? "hsl(38 92% 55%)"
                            : "hsl(0 75% 55%)",
                        }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border p-5"
              style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-3"
                style={{ color: "hsl(0 0% 55%)" }}>Missing Sections</div>
              {score.missing.length === 0 ? (
                <p className="text-xs" style={{ color: "hsl(142 70% 65%)" }}>All core sections covered.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {score.missing.map((m) => (
                    <span key={m} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium"
                      style={{ background: "hsl(0 75% 45% / 0.16)", color: "hsl(0 75% 78%)" }}>
                      <AlertTriangle size={10} /> {m}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl border p-5"
              style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-3"
                style={{ color: "hsl(0 0% 55%)" }}>Improvement Signals</div>
              {score.signals.length === 0 ? (
                <p className="text-xs" style={{ color: "hsl(142 70% 65%)" }}>No issues flagged.</p>
              ) : (
                <ul className="space-y-1.5 text-xs leading-relaxed" style={{ color: "hsl(0 0% 75%)" }}>
                  {score.signals.map((s, i) => (
                    <li key={i} className="flex gap-2">
                      <span style={{ color: "hsl(0 75% 65%)" }}>•</span><span>{s}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

type StageAction = "submit" | "approve" | "changes" | "reject" | "noop";

const STAGES: { key: string; label: string }[] = [
  { key: "topic",    label: "Topic" },
  { key: "draft",    label: "Draft Brief" },
  { key: "review",   label: "In Review" },
  { key: "approved", label: "Approved" },
  { key: "queue",    label: "Draft Queue" },
];

function currentStage(brief: ContentBrief | null, article: ArticleRow): number {
  if (!brief) return 0;
  if (brief.status === "rejected") return 1;
  if (brief.status === "draft") return 1;
  if (brief.status === "in_review") return 2;
  if (brief.status === "approved") {
    return ["draft", "human_review", "seo_review", "ready", "published"].includes(article.status) ? 4 : 3;
  }
  return 0;
}

const StageRail = ({
  brief, article, onAction,
}: { brief: ContentBrief | null; article: ArticleRow; onAction: (a: StageAction) => void | Promise<void> }) => {
  const stage = currentStage(brief, article);
  const status = brief?.status ?? null;

  return (
    <div className="rounded-xl border p-4 space-y-3"
      style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
      <div className="flex items-center gap-1 overflow-x-auto">
        {STAGES.map((s, i) => {
          const active = i <= stage;
          const isCurrent = i === stage;
          return (
            <div key={s.key} className="flex items-center gap-1 shrink-0">
              <div className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold"
                style={{
                  background: isCurrent ? "hsl(207 68% 50% / 0.18)" : active ? "hsl(142 70% 35% / 0.16)" : "hsl(0 0% 10%)",
                  color: isCurrent ? "hsl(207 68% 80%)" : active ? "hsl(142 70% 75%)" : "hsl(0 0% 50%)",
                  border: `1px solid ${isCurrent ? "hsl(207 68% 50% / 0.45)" : "hsl(0 0% 14%)"}`,
                }}>
                {active && !isCurrent ? <Check size={10} /> : <span className="tabular-nums">{i + 1}</span>}
                {s.label}
              </div>
              {i < STAGES.length - 1 && <ArrowRight size={11} style={{ color: "hsl(0 0% 30%)" }} />}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-2 border-t" style={{ borderColor: "hsl(0 0% 12%)" }}>
        {!brief && (
          <span className="text-xs flex items-center gap-1.5" style={{ color: "hsl(0 0% 60%)" }}>
            <FileText size={12} /> Use the form below to generate your brief.
          </span>
        )}
        {status === "draft" && (
          <>
            <Button size="sm" onClick={() => onAction("approve")}
              className="h-8 font-semibold" style={{ background: "hsl(142 60% 35%)", color: "white" }}>
              <Check size={12} className="mr-1.5" /> Approve and start drafting
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onAction("submit")}
              className="h-8" style={{ color: "hsl(0 0% 70%)" }}>
              <Send size={12} className="mr-1.5" /> Just submit for review
            </Button>
          </>
        )}
        {status === "in_review" && (
          <>
            <Button size="sm" onClick={() => onAction("approve")}
              className="h-8 font-semibold" style={{ background: "hsl(142 60% 35%)", color: "white" }}>
              <Check size={12} className="mr-1.5" /> Approve and start drafting
            </Button>
            <Button size="sm" variant="outline" onClick={() => onAction("changes")}
              className="h-8 border" style={{ borderColor: "hsl(38 92% 50% / 0.45)", color: "hsl(38 92% 75%)", background: "transparent" }}>
              <RotateCcw size={12} className="mr-1.5" /> Request Changes
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onAction("reject")}
              className="h-8" style={{ color: "hsl(0 75% 70%)" }}>
              <X size={12} className="mr-1.5" /> Reject
            </Button>
          </>
        )}
        {status === "approved" && (
          <span className="text-xs inline-flex items-center gap-1.5 rounded-full px-2.5 py-1"
            style={{ background: "hsl(142 70% 35% / 0.16)", color: "hsl(142 70% 75%)" }}>
            <Check size={11} /> Approved, open Draft Queue to generate the article.
          </span>
        )}
        {status === "rejected" && (
          <Button size="sm" variant="outline" onClick={() => onAction("changes")}
            className="h-8 border" style={{ borderColor: "hsl(0 0% 22%)", color: "hsl(0 0% 85%)", background: "transparent" }}>
            <RotateCcw size={12} className="mr-1.5" /> Re-open as Draft
          </Button>
        )}
      </div>
    </div>
  );
};

export default BriefGeneratorView;

